import type { FeatureFlagSet } from "../lib/featureFlags/types";

export const UNIZO_FEATURE_FLAGS = {
  continueMeeting: {
    label: "商談の続きを記録",
    description: "グループ内の前回商談から企業・担当者を引き継いで記録開始",
    defaultValue: false,
  },
  pushNotifications: {
    label: "Push通知(Chrome)",
    description:
      "Chromeブラウザへのweb push通知を有効化。設定画面にPushバナーと許可ボタンを表示",
    defaultValue: false,
  },
  meetingNlSearch: {
    label: "AIエージェント検索 (Stage 1)",
    description: "自然言語で商談を検索する API エンドポイントを有効化（メタデータフィルタ）",
    defaultValue: false,
  },
  meetingNlSearchVector: {
    label: "AIエージェント検索: ベクトル検索 (Stage 3)",
    description: "S3 Vectors による意味検索を有効化（meetingNlSearch との併用）",
    defaultValue: false,
  },
  meetingNlSearchRerank: {
    label: "AIエージェント検索: LLM rerank (Stage 3)",
    description: "検索結果を LLM で再ランキング（meetingNlSearch との併用）",
    defaultValue: false,
  },
  companyExternalSystemId: {
    label: "全店顧客番号入力（商談編集画面）",
    description:
      "商談編集画面に「全店顧客番号」(企業マスタ systemIntegrationId) の入力欄を表示し、保存時に企業マスタへ反映する。法人商談は取引先名の下、個人商談はお客様情報の下 (PRODUCT_DEV-952)。基幹システム側の顧客番号を商談に紐づけて運用するテナント向け。OFFで非表示・既存挙動のまま (PRODUCT_DEV-557)。",
    defaultValue: false,
  },
  customerNumberLabel: {
    label: "顧客番号の呼び名と番号検索",
    description:
      "管理者のテナント設定に「顧客番号の呼び名」の欄を出し、その呼び名を商談画面・候補・企業マスタ詳細・マイ顧客の編集・CSV 取り込みの列名に使う。顧客一覧で番号（企業マスタ systemIntegrationId）でも探せるようにする。「全店顧客番号入力」も ON のときだけ効く。OFF または呼び名が未設定なら既定の名前（外部システム企業ID など）で出す (PRODUCT_DEV-1239)。",
    defaultValue: false,
  },
  guideRenewal: {
    label: "使い方ガイド（新版）",
    description:
      "ONのテナントは刷新後の使い方ガイドを表示する。OFFは現行ガイド（回帰なし）。自社テナント先行検証→100%展開用 (PRODUCT_DEV-560)。",
    defaultValue: false,
  },
  sansanContactRegistration: {
    label: "Sansanコンタクト連携",
    description:
      "商談詳細に「Sansanへ登録」ボタンを表示し、商談をSansanのコンタクト(接点記録)として登録するAPI連携を有効化。自社テナント先行検証(PoC)→将来テナント別展開 (PRODUCT_DEV-531)。",
    defaultValue: false,
  },
  sansanExistingCardMatch: {
    label: "Sansan連携: 既存の名刺にそろえて送る",
    description:
      "Sansanへ登録する確認モーダルで、社外の担当者ごとに Sansan の既存の名刺の候補を出し、選んだ名刺の会社名・氏名・メールアドレスをそのまま送る（Sansan が同じ人・会社にまとめる）。選ばなかった担当者にもメールアドレスを足して送る。「Sansanコンタクト連携」が ON のテナントでだけ効く。OFF は今までどおり（回帰なし）(PRODUCT_DEV-1143)。",
    defaultValue: false,
  },
  schedulingCalendarSync: {
    label: "外部カレンダー連携（Google / Microsoft 365）",
    description:
      "利用者が自分の Google カレンダー / Microsoft 365 の予定表を1つだけつなぎ、その予定をマイ手帳の時間割に出す（読むだけ・予定は保存せず開くたびに読む・本人にしか見えない）。ON のテナントだけ、設定画面に「カレンダーをつなぐ／外す／つなぎ替える」と、管理者向けに自社の Google アプリを登録する欄が出る。⚠ 日程調整の空き時間の判定にはまだ使っていない（日程調整の「つなぐと候補日時が正確になります」の案内カードは、判定を作るまで出さない）。OFF は現行どおり（連携の入口も API も出さない）で回帰なし (PRODUCT_DEV-675 で置き場所だけ作り、PRODUCT_DEV-1225 で中身を入れた)。",
    defaultValue: false,
  },
  csMeetingNotification: {
    label: "商談記録の運営 Slack 通知",
    description:
      "このテナントで商談記録が作成されるたび、運営(カスタマーサクセス)の Slack チャンネルへ商談タイトル・プリセット名・記録モード・商談詳細リンクを送信する。商談タイトルには顧客名や個人名が含まれうるため、テナントの合意が取れている場合にのみ ON にすること。テナント向けの通知 Webhook (PRODUCT_DEV-379) とは別系統で、キーワード判定はせず全件が対象。OFFで送信しない (PRODUCT_DEV-495 / PRODUCT_DEV-735)。",
    defaultValue: false,
  },
  caseFolder: {
    label: "案件フォルダ（商談グループの改善）",
    description:
      "商談グループの呼び名を「案件フォルダ」にし、顧客の中で続いている話を入れておく場所として使えるようにする。①フォルダ詳細に要約をそのまま出し（最終更新の日時と再生成つき）、「この案件の続きを記録する」から記録を始められる（始めた記録はそのフォルダに入る。AI が要約を踏まえて対話するのは `continueMeeting` も ON のときだけ）。商談は顧客詳細と同じ数珠つなぎで並べる。②フォルダに相手の顧客を任意で持たせる（商談一覧の「まとめる」で選んだ商談が全部同じ顧客のときだけ自動で付ける。名前での推測はしない。フォルダ詳細の右上から後で選べる）。顧客詳細に「この顧客の案件フォルダ」と「案件フォルダを作る」を出す。③本登録した商談記録の中に任意の1行「どの案件の続きですか？」を出す（押さなければ何も起きない）。④次回アポの予定を押したとき、その顧客の案件フォルダの要約を「前回のおさらい」として出す（作ってある要約を出すだけで AI は新しく呼ばない）。商談一覧の「まとめる」は1件からできる。OFF は現行どおり（呼び名は「グループ」・まとめるは2件から）で回帰なし (PRODUCT_DEV-1214)。",
    defaultValue: false,
  },
  customerLookupUnizoWording: {
    label: "お客さまを確かめるときの言い方（Unizo の登録）",
    description:
      "録音中の AI がお客さま・企業を探す前の一言を「顧客マスタを確認しますね。」から「ユニゾーの登録を確認しますね。」に、法人で見つからなかったときの一言を「顧客マスタに登録がないので、仮登録しておきます」から「ユニゾーにまだ登録がないので、新しく登録しておきます」に変える（読みを決めるため話す文はカタカナ）。行内の CRM を見に行っているように聞こえないため。ONP の上書き（COMPANY_SEARCH / INDIVIDUAL_COMPANY_SEARCH）があってもその文の中の言い方を差し替える。OFF は現行どおりで回帰なし (PRODUCT_DEV-1234)。",
    defaultValue: false,
  },
  passwordReissue: {
    label: "パスワード再発行の依頼（メールの無い人）",
    description:
      "メールアドレスの無い利用者が、ログイン画面の「パスワードを忘れた」から管理者にログイン用コードの発行を依頼できる。管理者はユーザー管理の一番上に並ぶ依頼から2回のクリックでコードを出す。依頼しても・コードを出しても今のパスワードは使えるまま。ログイン画面（ポータル）もこのフラグで出し分ける。**既定は ON（2026-10-26 全社一斉に始めると決定）**。OFF にした会社は現行どおり「管理者に依頼してください」の案内だけ。Unizo を使っていない会社では ON でも始まらない (PRODUCT_DEV-1238)。",
    defaultValue: true,
  },
  offlineRecordingSave: {
    label: "電波が切れても録音を残す",
    description:
      "録音を送る前に端末（ブラウザ）にも保存し、電波が弱い・圏外で送れなかった録音を、つながったとき・画面に戻ったとき・次に開いたときに自動で送り直す（送れていない間は画面の上に「送れていない録音が◯件」の帯が出る）。まずは自由記録と音声メモが対象（対話モード・議事録は順に足す）。ログアウトするときに送れていない録音があれば、件数を出して確かめたうえで端末から消す（共有の端末に録音とお客さまの情報を残さないため）。iPhone の Safari ではホーム画面に置いていないと保存した中身が消されることがある。OFF にしても、すでに端末に残っている録音は帯から送れる・消せる。OFF は現行どおり（送れないと録音は消える）で回帰なし (PRODUCT_DEV-1270)。",
    defaultValue: false,
  },
  minutesJaEnMixed: {
    label: "議事録: 日本語と英語がまじる会議",
    description:
      "議事録（会議録音）の文字起こしを新しいモデル（gpt-transcribe）に替え、英語の発言が残るようにする。英語のある区切りだけ日本語に訳し、録音中の LIVE ログと全文は訳を主に原文を下に小さく出す。訳が1つでもあった会議だけ、清書で英語を消さない・議事録はすべて日本語で書く決まりを足し、議事録の上に「英語の発言を日本語に訳して含めています」の案内を出す。日本語だけの会議は文字起こしのモデルだけが変わる（指示・日本語固定は今のまま）。会議ごとの切り替えは無い。OFF は現行どおりで回帰なし (PRODUCT_DEV-1248)。",
    defaultValue: false,
  },
  transcribeGptTranscribe: {
    label: "文字起こしを新しいモデルに（録音ファイル）",
    description:
      "音声入力（検索・欄への音声入力）・音声メモ・自由記録の文字起こしを、gpt-4o-transcribe から新しいモデル（gpt-transcribe）に替える。gpt-4o-transcribe は 2027-02-26 に OpenAI の提供が終わるので、それまでに全社で ON にしてからこのフラグを外す。議事録は「議事録: 日本語と英語がまじる会議」のフラグで替わる。対話モードは「文字起こしを新しいモデルに（対話モード）」で別に替える。OFF は現行どおりで回帰なし (PRODUCT_DEV-1085)。",
    defaultValue: false,
  },
  realtimeTranscribeGptTranscribe: {
    label: "文字起こしを新しいモデルに（対話モード）",
    description:
      "対話モードの録音中、話した内容の文字起こし（対話ログ）を whisper-1 から新しいモデル（gpt-transcribe）に替える。ON の会社は Realtime 設定に保存されたモデルを見ずに必ず gpt-transcribe を使う。AI との会話そのもの（声で聞いて答える部分）は変わらない。whisper-1 は 2027-02-26 に OpenAI の提供が終わるので、それまでに全社で ON にしてからこのフラグを外す。OFF は現行どおりで回帰なし (PRODUCT_DEV-1085)。",
    defaultValue: false,
  },
  usageReport: {
    label: "利用状況の画面",
    description:
      "管理者トップに「利用状況」を出し、直近30日の使われ方を全体 → 部署 → 一人ずつで見られるようにする（利用・定着・品質・支援の4つの観点と前月比・判定）。見られるのはパワーユーザー以上。OFF はメニューに出ず、API も 404 を返す（現行どおりで回帰なし）(PRODUCT_DEV-1236)。",
    defaultValue: false,
  },
} as const satisfies FeatureFlagSet;

export type UnizoFeatureFlagKey = keyof typeof UNIZO_FEATURE_FLAGS;
